'use client'

import { memo, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Building2, Github, Mail, MessageCircle } from 'lucide-react'
import { useAnimate, type AnimationSequence } from 'framer-motion'
import { Button, buttonVariants } from '@/components/ui/button'
import { HighlighterItem, HighlightGroup, Particles } from '@/components/ui/highlighter'
import { cn } from '@/lib/utils'
import {
  mailtoEnterprise,
  mailtoSupport,
  NIN_SUPPORT_EMAIL,
} from './nin-contact-links'
import { NIN_GITHUB_URL, NIN_TELEGRAM_COMMUNITY_URL } from '@/app/nin/_components/nin-org-links'

type ContactTopic = {
  id: string
  label: string
  className: string
}

const CONTACT_TOPICS: ContactTopic[] = [
  {
    id: 'topic-enterprise',
    label: 'Enterprise',
    className: 'absolute right-12 top-10',
  },
  {
    id: 'topic-partnership',
    label: 'Partnership',
    className: 'absolute left-2 top-20',
  },
  {
    id: 'topic-support',
    label: 'Support',
    className: 'absolute bottom-20 right-1',
  },
  {
    id: 'topic-open-source',
    label: 'Open source',
    className: 'absolute bottom-12 left-14',
  },
]

const POINTER_SEQUENCE: AnimationSequence = [
  ['#nin-contact-pointer', { left: 200, top: 60 }, { duration: 0 }],
  ['#topic-enterprise', { opacity: 1 }, { duration: 0.3 }],
  [
    '#nin-contact-pointer',
    { left: 50, top: 102 },
    { at: '+0.5', duration: 0.5, ease: 'easeInOut' },
  ],
  ['#topic-enterprise', { opacity: 0.4 }, { at: '-0.3', duration: 0.1 }],
  ['#topic-partnership', { opacity: 1 }, { duration: 0.3 }],
  [
    '#nin-contact-pointer',
    { left: 224, top: 170 },
    { at: '+0.5', duration: 0.5, ease: 'easeInOut' },
  ],
  ['#topic-partnership', { opacity: 0.4 }, { at: '-0.3', duration: 0.1 }],
  ['#topic-support', { opacity: 1 }, { duration: 0.3 }],
  [
    '#nin-contact-pointer',
    { left: 88, top: 198 },
    { at: '+0.5', duration: 0.5, ease: 'easeInOut' },
  ],
  ['#topic-support', { opacity: 0.4 }, { at: '-0.3', duration: 0.1 }],
  ['#topic-open-source', { opacity: 1 }, { duration: 0.3 }],
  [
    '#nin-contact-pointer',
    { left: 200, top: 60 },
    { at: '+0.5', duration: 0.5, ease: 'easeInOut' },
  ],
  ['#topic-open-source', { opacity: 0.5 }, { at: '-0.3', duration: 0.1 }],
] 

export type NinContactSectionProps = {
  /** org = /nin, product = /ninxl landing */
  variant?: 'org' | 'product'
  className?: string
}

export const NinContactSection = memo(function NinContactSection({
  variant = 'org',
  className,
}: NinContactSectionProps) {
  const [scope, animate] = useAnimate()

  useEffect(() => {
    let active = true

    const runSequence = async () => {
      while (active) {
        await animate(POINTER_SEQUENCE)
      }
    }

    void runSequence()

    return () => {
      active = false
    }
  }, [animate])

  const title =
    variant === 'product' ? 'Questions about NINX?' : 'Questions about NIN?'
  const description =
    variant === 'product'
      ? 'Enterprise pilots, integrations, or product support: reach out anytime.'
      : 'Partnerships, open source, or product support: we would love to hear from you.'

  return (
    <section
      data-section="contact"
      className={cn('nin-contact-section relative mx-auto max-w-5xl', className)}
      aria-labelledby="nin-contact-heading"
    >
      <HighlightGroup className="group h-full">
        <div className="group/item h-full">
          <HighlighterItem className="rounded-3xl p-6">
            <div className="relative z-20 h-full overflow-hidden rounded-3xl border border-[color:var(--ninxl-8bit-border)] bg-[color:var(--ninxl-theme-bg-frame)]">
              <Particles
                className="absolute inset-0 -z-10 opacity-10 transition-opacity duration-1000 ease-in-out group-hover/item:opacity-100"
                quantity={160}
                color="#21728d"
                vy={-0.2}
              />

              <div className="flex justify-center">
                <div className="flex h-full flex-col justify-center gap-10 p-4 md:h-[300px] md:flex-row">
                  <div
                    className="relative mx-auto h-[270px] w-[300px] md:h-[270px] md:w-[300px]"
                    ref={scope}
                    aria-hidden
                  >
                    <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl border border-[color:var(--ninxl-8bit-border)] bg-[color:var(--ninxl-theme-atmosphere)] p-1.5 shadow-[2px_2px_0_0_var(--ninxl-8bit-dim)]">
                      <Image
                        src="/ninx_logo_light.svg"
                        alt=""
                        width={28}
                        height={28}
                        className="h-7 w-7 dark:hidden"
                      />
                      <Image
                        src="/ninx_logo_dark.svg"
                        alt=""
                        width={28}
                        height={28}
                        className="hidden h-7 w-7 dark:block"
                      />
                    </div>

                    {CONTACT_TOPICS.map((topic) => (
                      <div
                        key={topic.id}
                        id={topic.id}
                        className={cn(
                          topic.className,
                          'ninxl-hud-panel rounded-3xl px-2 py-1.5 text-[10px] opacity-50 md:text-xs',
                        )}
                      >
                        {topic.label}
                      </div>
                    ))}

                    <div id="nin-contact-pointer" className="absolute">
                      <svg
                        width="16.8"
                        height="18.2"
                        viewBox="0 0 12 13"
                        className="fill-[color:var(--ninxl-brand-teal,#21728d)]"
                        stroke="white"
                        strokeWidth="1"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 5.50676L0 0L2.83818 13L6.30623 7.86537L12 5.50676V5.50676Z"
                        />
                      </svg>
                      <span className="relative -top-1 left-3 rounded-3xl bg-[color:var(--ninxl-brand-teal,#21728d)] px-2 py-1 text-[10px] text-white">
                        NIN
                      </span>
                    </div>
                  </div>

                  <div className="-mt-20 flex h-full flex-col justify-center p-2 md:-mt-4 md:ml-10 md:w-[400px]">
                    <div className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">
                      GET IN TOUCH
                    </div>
                    <h2
                      id="nin-contact-heading"
                      className="ninxl-8bit-heading ninxl-theme-text pb-1 text-xl md:text-2xl"
                    >
                      {title}
                    </h2>
                    <p className="ninxl-8bit-body mb-5 text-sm md:text-base">{description}</p>

                    <div className="flex flex-wrap gap-2">
                      <Link href={mailtoSupport()}>
                        <Button className="ninxl-pixel-btn-primary rounded-md border-0">
                          Email us
                        </Button>
                      </Link>

                      {variant === 'product' ? (
                        <Link href={mailtoEnterprise()}>
                          <Button variant="outline" className="gap-2 rounded-md bg-transparent">
                            <Building2 className="h-4 w-4" />
                            Enterprise
                          </Button>
                        </Link>
                      ) : (
                        <Link href={NIN_GITHUB_URL} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" className="gap-2 rounded-md bg-transparent">
                            <Github className="h-4 w-4" />
                            GitHub
                          </Button>
                        </Link>
                      )}

                      <Link
                        href={mailtoSupport()}
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'icon' }),
                          'rounded-md bg-transparent',
                        )}
                        aria-label={`Email ${NIN_SUPPORT_EMAIL}`}
                      >
                        <Mail className="h-5 w-5" strokeWidth={1.5} />
                      </Link>

                      <Link
                        href={NIN_TELEGRAM_COMMUNITY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'icon' }),
                          'rounded-md bg-transparent',
                        )}
                        aria-label="Telegram community help"
                      >
                        <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                      </Link>
                    </div>

                    <p className="ninxl-8bit-label ninxl-theme-text-muted mt-4 text-[10px] md:text-xs">
                      {NIN_SUPPORT_EMAIL}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </HighlighterItem>
        </div>
      </HighlightGroup>
    </section>
  )
})
